import SearchBox from "@/common/SearchBox";
import { TruckIcon } from "@/Icon/TruckIcon";
import { createFileRoute } from "@tanstack/react-router";
import _ from "lodash";
import { motion, AnimatePresence } from "framer-motion";
import { useToastStore } from "&/toast";
import { useState, useRef, useEffect } from "react";
import {
  useBusinessAttendList,
  useUpdateBusinessAttend,
} from "~/services/business/main";
import {
  useBusinessAuthUserInfo,
  useReadBusinessActiveAlarm,
} from "~/services/business/auth";
import { AttendListItem } from "~/types/common";
import { useLoadingStore } from "&/loading";
// import SwipeableListItem from "@/business/notify/SwipeableListItem";
// import StudentListItem from "@/business/notify/StudentListItem";
import defaultPet from "$/defaultLogo.svg";
import { Dialog } from "@/Dialog";
// import { useAppExit } from "^/common/useAppExit";
// import { AppExitDialog } from "@/AppExitDialog";

export const Route = createFileRoute("/business/layout/to-school")({
  component: ToSchool,
  validateSearch: (
    search: Record<string, unknown>
  ): { attendanceType?: string; petName?: string; reservationSeq?: string } => ({
    attendanceType: search.attendanceType as string | undefined,
    petName: search.petName as string | undefined,
    reservationSeq: search.reservationSeq as string | undefined,
  }),
});

const options = [
  { value: "등원", label: "등원" },
  { value: "하원", label: "하원" },
];

interface Student {
  id: string;
  name: string;
  weight: string;
  status: string;
  img: string | null;
  petDrop: boolean;
  cdAttendStatus: string;
  dtCheckIn?: string | null;
  dtCheckOut?: string | null;
  timeDiff?: string | null;
}

/** "2026-05-09T09:21:00" → "09시 21분" */
const formatCheckTime = (dt?: string | null) => {
  if (!dt) return "";
  const d = new Date(dt);
  if (isNaN(d.getTime())) return "";
  const hh = String(d.getHours()).padStart(2, "0");
  const mm = String(d.getMinutes()).padStart(2, "0");
  return `${hh}시 ${mm}분`;
};

/** LocalTime "HH:mm:ss" → "7시간 20분" */
const formatTimeDiff = (diff?: string | null) => {
  if (!diff) return "";
  const [hStr, mStr] = diff.split(":");
  const hour = parseInt(hStr ?? "", 10);
  const min = parseInt(mStr ?? "", 10);
  if (Number.isNaN(hour) || Number.isNaN(min)) return "";
  if (hour === 0 && min === 0) return "";
  if (hour === 0) return `${min}분`;
  if (min === 0) return `${hour}시간`;
  return `${hour}시간 ${min}분`;
};
const CheckIcon = ({
  color,
  iconColor,
  size = "big",
}: {
  color: string;
  iconColor: string;
  size?: string;
}) => {
  if (size === "small") {
    return (
      <div
        className={`flex justify-center items-center w-4 h-4 rounded-full border-2 ${color} pb-[2px]`}
      >
        <svg
          className={`w-3 h-3 pt-[1px] ${iconColor}`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={3}
            d="M5 13l4 4L19 7"
          />
        </svg>
      </div>
    );
  } else {
    return (
      <div
        className={`flex justify-center items-center w-12 h-12 rounded-full border-2 ${color}`}
      >
        <svg
          className={`w-6 h-6 ${iconColor}`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={3}
            d="M5 13l4 4L19 7"
          />
        </svg>
      </div>
    );
  }
};

const StudentCard = ({
  student,
  attendanceType,
  onClick,
  isSelected,
}: {
  student: Student;
  attendanceType: string;
  onClick: (student: Student) => void;
  isSelected: boolean;
}) => {
  const borderColor =
    attendanceType === "등원" ? "border-orange-400" : "border-[#7D6DFF]";
  const checkIconColor =
    attendanceType === "등원" ? "text-orange-400" : "text-[#7D6DFF]";
  const truckIconColor = attendanceType === "등원" ? "#ED8432" : "#7D6DFF";
  const handleClick = () => {
    onClick(student);
  };
  return (
    <motion.div
      initial={{ opacity: 0, y: 20, scale: 0.95 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: -20, scale: 0.95 }}
      transition={{
        duration: 0.3,
        ease: "easeOut",
      }}
      onClick={handleClick}
      className={`bg-white rounded-xl p-3 flex items-end gap-4 mb-2 border-[1px] ${isSelected ? borderColor : "border-[#E0E0E0]"}`}
    >
      <div className="flex flex-shrink-0 justify-center items-center w-12 h-12">
        {isSelected ? (
          <CheckIcon color={borderColor} iconColor={checkIconColor} />
        ) : (
          <img
            src={student.img || defaultPet}
            alt={student.name}
            className="object-cover w-14 h-12 rounded-full"
          />
        )}
      </div>
      <div className="flex flex-col gap-2 w-full">
        <div className="flex gap-2 items-baseline">
          <p className="font-bold text-gray-800">{student.name}</p>
          <p className="text-sm text-gray-500">{student.weight}</p>
          {(student.cdAttendStatus == "ATTEND_STATUS_ATTENDED" &&
            attendanceType == "등원") ||
          (student.cdAttendStatus == "ATTEND_STATUS_DISMISSED" &&
            attendanceType == "하원") ? (
            <div className="flex justify-center items-end">
              <CheckIcon
                color={borderColor}
                iconColor={checkIconColor}
                size="small"
              />
            </div>
          ) : (
            <></>
          )}
        </div>
        <div className="flex gap-2 justify-between items-center">
          <p className="text-sm text-gray-600">{student.status}</p>
          <div className="flex flex-shrink-0 gap-2 items-center">
            {/* 등원시간 배지: dtCheckIn 이 있으면 상태 무관 표시.
                (이전엔 status===ATTENDED 로 제한돼 하원 완료 후엔 등원 이력이 보이지 않았음) */}
            {student.dtCheckIn && (
                <span className="text-[12px] text-gray-500 whitespace-nowrap">
                  {/* 공간 충분 시 (sm 이상): "등원시간 09시 21분" */}
                  <span className="hidden sm:inline">
                    등원시간 {formatCheckTime(student.dtCheckIn)}
                  </span>
                  {/* 공간 부족 시 (모바일): "09시 21분 등원" */}
                  <span className="inline sm:hidden">
                    {formatCheckTime(student.dtCheckIn)} 등원
                  </span>
                </span>
              )}
            {student.cdAttendStatus === "ATTEND_STATUS_DISMISSED" &&
              student.dtCheckOut && (
                <span className="text-[12px] text-gray-500 whitespace-nowrap">
                  {/* 공간 충분 시: "하원 시간 19시 10분 (7시간 20분 이용)" */}
                  <span className="hidden sm:inline">
                    하원 시간 {formatCheckTime(student.dtCheckOut)}
                    {formatTimeDiff(student.timeDiff) &&
                      ` (${formatTimeDiff(student.timeDiff)} 이용)`}
                  </span>
                  {/* 공간 부족 시: "19시 10분 하원(7시간 20분)" */}
                  <span className="inline sm:hidden">
                    {formatCheckTime(student.dtCheckOut)} 하원
                    {formatTimeDiff(student.timeDiff) &&
                      `(${formatTimeDiff(student.timeDiff)})`}
                  </span>
                </span>
              )}
            {student.petDrop && <TruckIcon color={truckIconColor} />}
          </div>
        </div>
      </div>
    </motion.div>
  );
};

function ToSchool() {
  const { add: addToast } = useToastStore();

  // const { showExitDialog, hideExitConfirmation, confirmAppExit } = useAppExit();

  // 푸시 딥링크(PUSH_DAYCARE_END_BUSINESS)에서 전달되는 파라미터
  const { attendanceType: attendanceTypeParam, petName: petNameParam } =
    Route.useSearch();

  // 초기 등/하원 구분: 딥링크 파라미터 우선, 없으면 시간대 기준
  const initialAttendanceType =
    attendanceTypeParam === "등원" || attendanceTypeParam === "하원"
      ? attendanceTypeParam
      : new Date().getHours() < 12
        ? "등원"
        : "하원";

  const [attendanceType, setAttendanceType] = useState(initialAttendanceType);
  const [mainColor, setMainColor] = useState(() =>
    initialAttendanceType === "등원"
      ? { main: "[#ED8432]", sub: "[#F88D391A]" }
      : { main: "[#7D6DFF]", sub: "[#7D6DFF1A]" }
  );

  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [selected, setSelected] = useState(0);
  const [tabList, setTabList] = useState(() =>
    initialAttendanceType === "등원"
      ? [
          { label: "미등원", value: "ATTEND_STATUS_NOT_ATTENDED" },
          // "등원 완료" = 오늘 등원한 아이 전체 — 이후 하원 처리돼도 이 탭에 남는다.
          // (이전엔 ATTENDED 만 매칭돼 하원 완료 후 목록에서 사라지는 문제 있었음)
          // BE 필터에서 DISMISSED_ALL = ATTENDED + DISMISSED 로 정의됨.
          { label: "등원 완료", value: "ATTEND_STATUS_DISMISSED_ALL" },
          { label: "전체", value: "ATTEND_STATUS_ATTENDED_ALL" },
        ]
      : [
          { label: "미하원", value: "ATTEND_STATUS_NOT_DISMISSED" },
          { label: "하원 완료", value: "ATTEND_STATUS_DISMISSED" },
          { label: "전체", value: "ATTEND_STATUS_DISMISSED_ALL" },
        ]
  );

  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isActivationApprovedVisible, setIsActivationApprovedVisible] =
    useState(false);
  const [hasShownActivationApproved, setHasShownActivationApproved] =
    useState(false);
  const { data: businessUserInfo } = useBusinessAuthUserInfo(true);
  const { mutateAsync: readActiveAlarmAsync } = useReadBusinessActiveAlarm();

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsDropdownOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [dropdownRef]);
  useEffect(() => {
    setSelected(0);
  }, [attendanceType]);

  useEffect(() => {
    if (businessUserInfo?.activeCompany && !hasShownActivationApproved) {
      setIsActivationApprovedVisible(true);
    }
  }, [businessUserInfo?.activeCompany, hasShownActivationApproved]);

  // 현재 선택된 탭의 attendStatus 값 가져오기
  const currentAttendStatus = tabList[selected]?.value;
  const currentTabLabel = tabList[selected]?.label;

  // API 호출 - 선택된 탭에 따라 다른 attendStatus로 조회
  const {
    data: attendListData,
    isLoading,
    error,
    isFetching,
    isSuccess,
  } = useBusinessAttendList(currentAttendStatus);

  // 로딩 스토어 사용
  const { loading, toggleLoading } = useLoadingStore();

  const [students, setStudents] = useState<AttendListItem[]>([]);
  const [selectedStudents, setSelectedStudents] = useState<AttendListItem[]>(
    [],
  );
  // 푸시 딥링크로 강아지 이름이 전달되면 검색어로 초기화
  const [searchTerm, setSearchTerm] = useState(petNameParam ?? "");

  // React Query 로딩 상태와 로딩 스토어 연동
  useEffect(() => {
    toggleLoading(isLoading || isFetching);
  }, [isLoading, isFetching, toggleLoading]);

  useEffect(() => {
    // 데이터가 성공적으로 로드되었고 fetching 중이 아닐 때만 업데이트
    if (isSuccess && !isFetching && attendListData) {
      let newData: AttendListItem[] = [];

      if (attendListData?.data) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const apiData = attendListData.data as any;

        // API 응답 구조가 다양할 수 있으므로 여러 경우를 체크
        if (Array.isArray(apiData)) {
          newData = apiData;
        } else if (
          apiData.props?.[0]?.datas &&
          Array.isArray(apiData.props[0].datas)
        ) {
          newData = apiData.props[0].datas;
        } else if (apiData.datas && Array.isArray(apiData.datas)) {
          newData = apiData.datas;
        } else if (apiData.list && Array.isArray(apiData.list)) {
          newData = apiData.list;
        } else if (apiData.items && Array.isArray(apiData.items)) {
          newData = apiData.items;
        }
      }

      setStudents(_.cloneDeep(newData));
    }
  }, [attendListData, isSuccess, isFetching, currentAttendStatus]);

  // 검색어에 따른 필터링된 학생 목록
  const filteredStudents = students.filter((student) => {
    if (!searchTerm) return true;

    const searchLower = searchTerm.toLowerCase();
    return (
      student.petName?.toLowerCase().includes(searchLower) ||
      student.reservation?.toLowerCase().includes(searchLower)
    );
  });

  // const handleApprove = () => {
  //   addToast("예약이 승인되었습니다.");
  // };

  // const handleCancel = () => {
  //   addToast("예약이 취소되었습니다.");
  // };

  const handleSelect = (value: string) => {
    // 드롭박스 변경 시 즉시 기존 데이터 초기화
    if (attendanceType === value) {
      return;
    }
    setStudents([]);
    setSelectedStudents([]);
    setAttendanceType(value);
    setMainColor(
      value == "등원"
        ? { main: "[#ED8432]", sub: "[#F88D391A]" }
        : { main: "[#7D6DFF]", sub: "[#7D6DFF1A]" },
    );
    setTabList(
      value == "등원"
        ? [
            { label: "미등원", value: "ATTEND_STATUS_NOT_ATTENDED" },
            { label: "등원 완료", value: "ATTEND_STATUS_ATTENDED" },
            { label: "전체", value: "ATTEND_STATUS_ATTENDED_ALL" },
          ]
        : [
            { label: "미하원", value: "ATTEND_STATUS_NOT_DISMISSED" },
            { label: "하원 완료", value: "ATTEND_STATUS_DISMISSED" },
            { label: "전체", value: "ATTEND_STATUS_DISMISSED_ALL" },
          ],
    );
  };

  const handleClick = (item: AttendListItem) => {
    if (attendanceType == "하원") {
      if (item.reservation == "미용") {
        return;
      }
    }
    if (currentTabLabel == "전체" || currentTabLabel == "등원 완료") {
      return;
    }
    if (
      selectedStudents.some(
        (student) => student.reservDetailSeq === item.reservDetailSeq,
      )
    ) {
      setSelectedStudents(
        selectedStudents.filter(
          (student) => student.reservDetailSeq !== item.reservDetailSeq,
        ),
      );
    } else {
      setSelectedStudents([...selectedStudents, item]);
    }
  };

  // ── 일괄 처리 대상 ───────────────────────────────────────────────
  // 선택이 있으면 선택분, 없으면 "현재 화면에 보이는" 목록을 대상으로 한다.
  // filteredStudents(검색 반영) 를 쓰는 이유: 보고 있는 것과 처리되는 것이 일치해야 한다.
  // (기존에는 students 전체를 대상으로 삼아, 검색 중에도 화면에 없는 아이까지 처리됐다)
  const isSearching = searchTerm.trim().length > 0;
  const bulkTargets =
    selectedStudents.length > 0 ? selectedStudents : filteredStudents;
  const bulkCount = bulkTargets.length;

  // 수행할 동작 종류. 문자열 매칭 대신 이 값으로 분기한다.
  type AttendActionKind = "ATTEND" | "DISMISS" | "DISMISS_CANCEL";

  const getActionKind = (): AttendActionKind | null => {
    if (attendanceType === "등원") {
      if (currentTabLabel === "미등원") return "ATTEND";
      return null; // "등원 완료" / "전체" 탭에서는 일괄 동작 없음
    }
    if (attendanceType === "하원") {
      if (currentTabLabel === "미하원") return "DISMISS";
      if (currentTabLabel === "하원 완료") return "DISMISS_CANCEL";
      return null; // "전체" 탭에서는 일괄 동작 없음
    }
    return null;
  };

  // 대상 범위를 나타내는 접두사. 검색 중임을 명시해 오조작을 막는다.
  const getScopeLabel = () => {
    if (selectedStudents.length > 0) return "선택";
    if (isSearching) return "검색결과 모두";
    return "모두";
  };

  // 버튼 텍스트 결정 함수 — 대상 건수를 함께 노출한다.
  const getButtonText = () => {
    const kind = getActionKind();
    if (!kind || bulkCount === 0) return "";

    const scope = getScopeLabel();
    const actionLabel =
      kind === "ATTEND"
        ? "등원"
        : kind === "DISMISS"
          ? "하원"
          : "하원 취소";

    return `${scope} ${actionLabel} (${bulkCount})`;
  };

  const getModalText = () => {
    const kind = getActionKind();
    if (!kind || bulkCount === 0) return { title: "", message1: "" };

    const scope = getScopeLabel();
    const actionLabel =
      kind === "ATTEND"
        ? "등원"
        : kind === "DISMISS"
          ? "하원"
          : "하원 취소";

    // 대상이 무엇인지 문장으로 분명히 밝힌다.
    const subject =
      selectedStudents.length > 0
        ? `선택한 ${bulkCount}마리`
        : isSearching
          ? `검색된 ${bulkCount}마리`
          : `${bulkCount}마리`;

    return {
      title: `${scope} ${actionLabel}`,
      message1: `${subject}를 ${actionLabel} 처리할까요?`,
    };
  };

  // 출석 상태 업데이트 mutation 훅
  const updateAttendMutation = useUpdateBusinessAttend();

  const handleApprove = () => {
    setIsModalVisible(true);
  };

  const handleModalClose = () => {
    setIsModalVisible(false);
  };

  const handleConfirm = () => {
    setIsModalVisible(false);

    const kind = getActionKind();
    if (!kind || bulkTargets.length === 0) return;

    // 상태를 직접 변형하지 않고 요청용 사본을 만든다.
    // (기존에는 state 객체를 그대로 수정해, API 가 실패해도 화면은 성공으로 보였다)
    const request: AttendListItem[] = bulkTargets.map((student) => {
      if (kind === "ATTEND" || kind === "DISMISS_CANCEL") {
        return { ...student, cdAttendStatus: "ATTEND_STATUS_ATTENDED" };
      }
      // DISMISS — 미용은 하원 개념이 없어 등원 상태를 유지한다.
      return {
        ...student,
        cdAttendStatus:
          student.reservation === "미용"
            ? "ATTEND_STATUS_ATTENDED"
            : "ATTEND_STATUS_DISMISSED",
      };
    });

    const actionLabel =
      kind === "ATTEND" ? "등원" : kind === "DISMISS" ? "하원" : "하원 취소";

    updateAttendMutation.mutate(request, {
      onSuccess: () => {
        // 목록은 훅의 invalidateQueries 로 서버 기준 재조회된다.
        setSelectedStudents([]);
        addToast(`${request.length}마리 ${actionLabel} 처리했습니다.`);
      },
      onError: (error) => {
        console.error("출석 상태 업데이트 실패:", error);
        addToast(`${actionLabel} 처리에 실패했습니다. 잠시 후 다시 시도해주세요.`);
      },
    });
  };

  const handleActivationApprovedConfirm = async () => {
    try {
      await readActiveAlarmAsync();
    } catch (error) {
      console.error("활성화 승인 알림 읽음 처리 실패:", error);
    } finally {
      setHasShownActivationApproved(true);
      setIsActivationApprovedVisible(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
      className="flex-1 min-h-0 overflow-y-auto bg-gray-50"
    >
      <div className="flex flex-none gap-2 items-center p-5 text-lg font-semibold bg-white">
        <p>오늘의</p>
        <div className="relative" ref={dropdownRef}>
          <button
            onClick={() => setIsDropdownOpen(!isDropdownOpen)}
            className={`flex items-center gap-1  text-white rounded-lg px-3 py-1 ${"bg-" + mainColor.main} `}
          >
            <span>{attendanceType}</span>
            <svg
              className={`w-5 h-5 transition-transform ${
                isDropdownOpen ? "rotate-180" : ""}`}
              fill="currentColor"
              viewBox="0 0 20 20"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                fillRule="evenodd"
                d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                clipRule="evenodd"
              />
            </svg>
          </button>
          {isDropdownOpen && (
            <ul className="absolute z-10 mt-1 w-full bg-white rounded-md border shadow-lg">
              {options.map((option) => (
                <li
                  key={option.value}
                  onClick={() => {
                    handleSelect(option.value);
                    setIsDropdownOpen(false);
                    // setAttendanceType(option.value);
                    // setIsDropdownOpen(false);
                    // setMainColor(option.value == "등원" ? {main:"bg-[#ED8432]" ,sub:"bg-[#F88D391A]"} : {main:"bg-[#7D6DFF]" ,sub:"bg-[#7D6DFF1A]"})
                  }}
                  className="px-3 py-1 text-center text-gray-700 cursor-pointer hover:bg-gray-100"
                >
                  {option.label}
                </li>
              ))}
            </ul>
          )}
        </div>
        <p>체크</p>
      </div>
      <div className="px-5 pt-2">
        <div
          className={`rounded-xl p-4 flex justify-between items-center ${"bg-" + mainColor.sub}`}
        >
          <p className="text-[16px] text-gray-700">
            오늘의 등원 예정 댕댕이는?
          </p>
          <div className="flex gap-1 items-baseline">
            <span className="text-[20px] font-bold text-black">
              {attendListData?.data?.completeCount}
            </span>
            <span className="text-[20px] text-gray-500">
              /{attendListData?.data?.totalCount}
            </span>
            <span className="text-base text-gray-500">마리</span>
          </div>
        </div>
      </div>
      <div className="px-4 py-3">
        <SearchBox
          onSearch={(value) => setSearchTerm(value)}
          placeholder="반려동물 이름 또는 서비스로 검색"
        />
      </div>
      <div className="px-4">
        <div className="flex gap-2 justify-center py-2">
          {tabList.map((item, idx) => (
            <motion.button
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{
                duration: 0.3,
                delay: idx * 0.1,
                ease: "easeOut",
              }}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              key={item.value}
              className={`px-5 py-1.5 rounded-full border font-medium text-[#666666] w-full
                ${
                  selected === idx && attendanceType == "등원"
                    ? "border-orange-400 text-orange-500"
                    : selected === idx && attendanceType == "하원"
                      ? "border-[#7D6DFF] text-[#7D6DFF]"
                      : selected === idx
                        ? "border-orange-400 text-orange-500"
                        : "border-gray-300 text-gray-500"
                }
                bg-white`}
              style={selected === idx ? { borderWidth: 1 } : {}}
              onClick={() => {
                // 탭 변경 시 즉시 기존 데이터 초기화
                if (selected != idx) {
                  setStudents([]);
                  setSelected(idx);
                  setSelectedStudents([]);
                }
              }}
            >
              {item.label}
            </motion.button>
          ))}
        </div>
      </div>
      <div className="px-4 py-2 bg-[#F5F5F5] border-t-[1px] border-[#E0E0E0] min-h-[40vh]">
        <div className="flex gap-2 justify-end mb-2">
          {getButtonText() && (
            // 일괄 등/하원 처리 버튼 — 텍스트로만 보여 눌러야 하는 줄 모르는 문제가 있어
            // 탭과 같은 pill 형태의 버튼으로 노출한다.
            //   등원/하원: 해당 모드 색으로 채운 주버튼
            //   하원 취소: 되돌리기 성격이라 같은 색 외곽선 버튼
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={handleApprove}
              className={`px-4 py-1.5 rounded-full text-sm font-semibold shadow-sm ${
                getActionKind() === "DISMISS_CANCEL"
                  ? "bg-white border border-[#7D6DFF] text-[#7D6DFF]"
                  : attendanceType === "등원"
                    ? "bg-orange-400 text-white"
                    : "bg-[#7D6DFF] text-white"
              }`}
            >
              {getButtonText()}
            </motion.button>
          )}
        </div>

        {/* API 데이터 렌더링 */}
        {loading ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="flex flex-col justify-center items-center h-32"
          >
            {/* <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
              className="mb-3 w-8 h-8 rounded-full border-2 border-orange-200 border-t-orange-400"
            /> */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="text-sm text-gray-500"
            >
              학생 정보를 불러오는 중...
            </motion.div>
          </motion.div>
        ) : error ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex justify-center items-center h-32"
          >
            <div className="text-red-500">
              데이터를 불러오는데 실패했습니다.
            </div>
          </motion.div>
        ) : filteredStudents && filteredStudents.length > 0 ? (
          <AnimatePresence mode="popLayout">
            {filteredStudents.map((attendItem) => (
              <StudentCard
                key={attendItem.reservDetailSeq}
                student={{
                  id: attendItem.reservDetailSeq.toString(),
                  name: attendItem.petName,
                  weight: attendItem.petWeight, // API에서 weight 정보가 없으므로 기본값
                  status: attendItem.reservation,
                  img: attendItem.thumbnailUrl, // 기본 이미지
                  cdAttendStatus: attendItem.cdAttendStatus,
                  petDrop: attendItem.petDrop,
                  dtCheckIn: attendItem.dtCheckIn,
                  dtCheckOut: attendItem.dtCheckOut,
                  timeDiff: attendItem.timeDiff,
                }}
                attendanceType={attendanceType}
                onClick={() => handleClick(attendItem)}
                isSelected={selectedStudents.some(
                  (student) =>
                    student.reservDetailSeq === attendItem.reservDetailSeq,
                )}
              />
            ))}
          </AnimatePresence>
        ) : searchTerm && filteredStudents.length === 0 ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex justify-center items-center h-32"
          >
            <div className="text-gray-500">검색 결과가 없습니다.</div>
          </motion.div>
        ) : (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex justify-center items-center h-32"
          >
            <div className="text-gray-500">등록된 학생이 없습니다.</div>
          </motion.div>
        )}
      </div>
      <Dialog
        visible={isModalVisible}
        onClose={handleModalClose}
        persist={false}
      >
        <div className="flex flex-col gap-1">
          <div className="flex flex-col mb-2">
            <div className="text-lg font-semibold text-center">
              {getModalText().title}
            </div>
          </div>
          <div className="flex flex-col mb-2">
            <div className="text-sm text-center text-[#666666]">
              {getModalText().message1}
            </div>
          </div>
          <div className="flex gap-3 mt-2">
            <button
              className="flex-1 py-2 font-semibold text-gray-600 border border-[#E5E5E5] rounded-full"
              onClick={() => setIsModalVisible(false)}
            >
              취소
            </button>
            <button
              className="flex-1 py-2 font-semibold text-white bg-orange-400 rounded-full"
              onClick={handleConfirm}
            >
              확인
            </button>
          </div>
        </div>
      </Dialog>

      <Dialog
        visible={isActivationApprovedVisible}
        onClose={handleActivationApprovedConfirm}
        persist={false}
      >
        <div className="flex flex-col gap-1">
          <div className="flex flex-col mb-2">
            <div className="text-lg font-semibold text-center">알림</div>
          </div>
          <div className="flex flex-col mb-2">
            <div className="text-sm text-center text-[#666666]">
              서비스 활성화가 승인되었습니다.
            </div>
          </div>
          <div className="flex gap-3 mt-2">
            <button
              className="flex-1 py-2 font-semibold text-white bg-orange-400 rounded-full"
              onClick={() => void handleActivationApprovedConfirm()}
            >
              확인
            </button>
          </div>
        </div>
      </Dialog>

      {/* <AppExitDialog
        visible={showExitDialog}
        onConfirm={confirmAppExit}
        onCancel={hideExitConfirmation}
      /> */}
    </motion.div>
  );
}
